import { Box, Button, Icon, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import type { Location } from 'history'
import React from 'react'
import { Prompt } from 'react-router-dom'
import { useNavigate } from 'react-router-dom-v5-compat'

import CopyBox from '@/app/component/atom/copy-box'
import EditableText from '@/app/component/atom/form/editable-text'
import Loader from '@/app/component/atom/loader'
import FloatingSaveBarCont from '@/app/component/layout/floating-save-bar'
import IconText from '@/app/component/layout/icon-text'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import CampaignSidebar from '@/app/module/campaigns/component/campaign-sidebar'
import { getChatbotUnsavedChangesConfirmation } from '@/app/module/campaigns/component/confirmations'
import { getCampaignItem } from '@/app/module/campaigns/component/item-helpers'
import ItemProvider from '@/app/module/campaigns/component/item-provider'
import { useChatbotChannel } from '@/app/module/campaigns/hooks/use-chatbot-channel'
import { useCreateCampaign } from '@/app/module/campaigns/hooks/use-create-campaign'
import { useGetCampaign } from '@/app/module/campaigns/hooks/use-get-campaign'
import { useLaunchCampaign } from '@/app/module/campaigns/hooks/use-launch-campaign'
import { useTestChatbot } from '@/app/module/campaigns/hooks/use-test-chatbot'
import { useUpdateCampaign } from '@/app/module/campaigns/hooks/use-update-campaign'
import {
  CampaignTemplate,
  CampaignTemplateType,
  ChatbotCampaignItem,
  ChatbotConfigs,
} from '@/app/module/campaigns/types'
import { fromCampaign } from '@/app/module/campaigns/utils/campaign-content'
import { formatDT } from '@/app/service/util/format'

import ChatbotContent from './content'
import ChatbotPhoneNumber from './phone-number'
import ChatbotTester, { ChatMessage } from './tester'

type Props = { campaignId?: number | string }

const steps = [
  { key: 'content', label: 'Content', icon: 'edit' },
  { key: 'tester', label: 'Tester', icon: 'smart_toy' },
  { key: 'number', label: 'Phone Numbers', icon: 'ad_units' },
]

const ChatbotEditor: React.FC<Props> = ({ campaignId }) => {
  const navigate = useNavigate()
  const createConfirmation = useConfirmation()
  const { data: remoteCampaign, isFetching } = useGetCampaign(campaignId)
  const [localItem, setLocalItem] = React.useState<ChatbotCampaignItem>()
  const [changed, setChanged] = React.useState(false)
  const [confirmationPending, setConfirmationPending] = React.useState(false)
  const [exitLocation, setExitLocation] = React.useState<Partial<Location>>()
  const [activeStep, setActiveStep] = React.useState(0)
  const [chatSession, setChatSession] = React.useState<{ contactId: number; runId: number }>()

  React.useEffect(() => {
    if (!changed) return
    const handleUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [changed])

  React.useEffect(() => {
    if (!exitLocation) return
    navigate(exitLocation, { state: exitLocation.state })
    setExitLocation(undefined)
  }, [exitLocation, navigate])

  const confirmExit = async (destination: Location) => {
    setConfirmationPending(true)
    try {
      await createConfirmation(
        getChatbotUnsavedChangesConfirmation(() => {
          setChanged(false)
          setExitLocation(destination)
        }),
      )
    } finally {
      setConfirmationPending(false)
    }
  }

  // Initialize for create flow
  React.useEffect(() => {
    if (campaignId || localItem) {
      return
    }
    const time = formatDT('now')
    setLocalItem((s) => ({
      ...s,
      ...getCampaignItem({
        type: CampaignTemplateType.Chatbot,
      }),
      name: `Chatbot Campaign (${time})`,
    }))
  }, [campaignId, localItem])

  // Initialize for edit flow
  React.useEffect(() => {
    if (!campaignId || !remoteCampaign) {
      return
    }
    setLocalItem(remoteCampaign as ChatbotCampaignItem)
  }, [campaignId, remoteCampaign])

  const updateItem = React.useCallback((partial: Partial<ChatbotCampaignItem>) => {
    setLocalItem((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        ...partial,
      }
    })
    setChanged(true)
  }, [])

  const [createCampaign, { isLoading: isCreating }] = useCreateCampaign()
  const [launchCampaign, { isLoading: isLaunching }] = useLaunchCampaign()
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateCampaign()
  const handleSave = React.useCallback(async () => {
    if (!localItem) {
      return
    }

    const dto = fromCampaign(localItem, CampaignTemplate.Chatbot)

    if (!campaignId) {
      const created = await createCampaign(dto)
      if (created?.id) {
        setChanged(false)
        setExitLocation({ pathname: `/campaigns/edit/${created.id}` })
        await launchCampaign(created.id)
      }
      return
    }
    const updated = await updateCampaign(campaignId, dto)
    if (updated) {
      setChanged(false)
      // cleanup chat session on changes
      setChatSession(undefined)
    }
  }, [campaignId, localItem, createCampaign, launchCampaign, updateCampaign])

  const handleBack = React.useCallback(() => {
    navigate('/campaigns')
  }, [navigate])

  const handleConfigsChange = React.useCallback(
    (config: ChatbotConfigs) => {
      if (!localItem) return

      updateItem({
        variables: {
          ...localItem.variables,
          config,
        },
      })
    },
    [localItem, updateItem],
  )

  const { contact: testContact, loading: isSubscribing, startTest } = useTestChatbot(campaignId)
  const {
    messages: wsMessages,
    subId,
    typing,
    clear,
    send: sendChatMessage,
  } = useChatbotChannel(campaignId ? Number(campaignId) : undefined)

  const handleSubscribeTestContact = React.useCallback(() => {
    if (!campaignId || isSubscribing) return
    clear()
    startTest()
  }, [campaignId, isSubscribing, clear, startTest])

  React.useEffect(() => {
    if (!testContact?.id) {
      return
    }

    setChatSession({
      contactId: testContact.id,
      runId: 0,
    })
  }, [testContact])

  React.useEffect(() => {
    if (!subId) {
      return
    }

    setChatSession((s) => {
      if (!s || s.runId === subId) {
        return s
      }

      return {
        ...s,
        runId: subId,
      }
    })
  }, [subId])

  const campaignName = localItem?.name || ''
  const loading = isFetching || isCreating || isLaunching || isUpdating
  let disabledReason: 'not-launched' | 'no-session' | undefined
  if (!campaignId || !localItem?.launched) {
    disabledReason = 'not-launched'
  } else if (!chatSession) {
    disabledReason = 'no-session'
  }

  const configs = localItem?.variables?.config

  const testerMessages: ChatMessage[] = React.useMemo(
    () =>
      wsMessages
        .filter((m) => !!m.msg) // Some special messages may not have msg (e.g. typing indicators)
        .map((m) => ({
          role: (m.direction === 'egress' ? 'assistant' : 'user') as any,
          content: m.msg!,
        })),
    [wsMessages],
  )
  const senderIdVal = localItem?.variables?.senderIdWhatsapp || ''

  if (!localItem) {
    return null
  }

  return (
    <ItemProvider item={localItem} setItem={setLocalItem}>
      <Prompt
        when={changed && !exitLocation}
        message={(destination) => {
          if (!confirmationPending) confirmExit(destination)
          return false
        }}
      />
      <Wrapper>
        <CampaignSidebar
          campaignName={campaignName || 'Chatbot Campaign'}
          options={steps.map((s, i) => ({
            name: s.key,
            label: s.label,
            icon: s.icon,
            active: activeStep === i,
            onClick: () => setActiveStep(i),
          }))}
          onBack={handleBack}
          footer={
            <SidebarFooter>
              <Button
                id="chatbot-campaign-save-sidebar"
                color="primary"
                disabled={loading || !changed}
                startIcon={<Icon>save</Icon>}
                variant="outlined"
                onClick={handleSave}
              >
                Save
              </Button>
            </SidebarFooter>
          }
        />
        <Right>
          <HeaderBar>
            <Typography component="div" variant="h6" color="textSecondary" sx={{ flex: 1 }}>
              <EditableText
                maxLength={200}
                displayText={`${campaignId ? 'Edit' : 'Create'} Chatbot Campaign "${campaignName}"`}
                value={campaignName}
                onChange={(name) => updateItem({ name })}
              />
            </Typography>
            {campaignId && <CopyBox text={String(campaignId)} title="Campaign ID" />}
          </HeaderBar>
          <Content>
            {activeStep === 0 && (
              <ChatbotContent
                campaignId={campaignId}
                name={campaignName}
                configs={configs}
                onConfigsChange={handleConfigsChange}
              />
            )}
            {activeStep === 1 && (
              <ChatbotTester
                disabledReason={disabledReason}
                messages={testerMessages}
                typing={typing}
                onSend={(msg) => sendChatMessage(msg)}
                onStartTesting={handleSubscribeTestContact}
              />
            )}
            {activeStep === 2 && (
              <ChatbotPhoneNumber
                senderId={senderIdVal}
                onChange={(senderId) => {
                  if (!localItem) return

                  updateItem({
                    variables: {
                      ...localItem.variables,
                      senderIdWhatsapp: senderId,
                      defaultSenderId: false,
                    },
                  })
                }}
              />
            )}
            <FloatingSaveBarCont>
              <BarInner>
                {activeStep === 0 && (
                  <Button id="chatbot-step-next" variant="contained" color="primary" onClick={() => setActiveStep(1)}>
                    Next
                  </Button>
                )}
                {activeStep === 1 && (
                  <>
                    <Button id="chatbot-step-next" variant="contained" color="primary" onClick={() => setActiveStep(2)}>
                      Next
                    </Button>
                    <Button id="chatbot-step-back" variant="outlined" color="primary" onClick={() => setActiveStep(0)}>
                      <span>Back</span>
                    </Button>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <Button
                      id="chatbot-campaign-save"
                      data-testid="chatbot-campaign-save"
                      variant="contained"
                      color="primary"
                      disabled={loading || !changed}
                      onClick={handleSave}
                      sx={{ ml: 2 }}
                    >
                      <IconText>
                        <Icon>save</Icon>
                        <span>Save</span>
                      </IconText>
                    </Button>
                    <Button id="chatbot-step-back" variant="outlined" color="primary" onClick={() => setActiveStep(1)}>
                      <span>Back</span>
                    </Button>
                  </>
                )}
              </BarInner>
            </FloatingSaveBarCont>
          </Content>
        </Right>
        {loading && <Loader />}
      </Wrapper>
    </ItemProvider>
  )
}

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: 'calc(100vh - 64px)',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}))

const Right = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  minHeight: 0,
  minWidth: 0,
  overflowY: 'hidden',
  [theme.breakpoints.up('md')]: {
    height: '100%',
  },
  [theme.breakpoints.down('md')]: {
    overflowY: 'auto',
  },
}))

const Content = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: 16,
  minHeight: 0, // it's auto by default, which means it can't be smaller than its content
  gap: 16,
}))

const BarInner = styled(Box)(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'row-reverse',
  gap: 8,
  justifyContent: 'space-between',
  padding: '0 1rem',
}))

const HeaderBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2, 0, 2),
  gap: theme.spacing(2),
}))

const SidebarFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: theme.spacing(1),
  width: '100%',
}))

export default ChatbotEditor
